import React, { useState, useEffect } from 'react';
import useLoyaltyStore from './useLoyaltyStore';
import AnimatedModal from '../shared/AnimatedModal';
import ImageDropzone from '../shared/ImageDropzone';

const PartnerFormModal = () => {
  const { closeModal, modalMode, editingItem, savePartner } = useLoyaltyStore();
  const isEdit = modalMode === 'edit';

  const [form, setForm] = useState({
    name: '', partner_type: 'external_partner', description: '', website_url: '', contact_person: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && editingItem) {
      setForm({
        name: editingItem.name || '',
        partner_type: editingItem.partner_type || 'external_partner',
        description: editingItem.description || '',
        website_url: editingItem.website_url || '',
        contact_person: editingItem.contact_person || '',
      });
      if (editingItem.logo_url) setLogoPreview(editingItem.logo_url);
    }
  }, [isEdit, editingItem]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleFileSelect = (file) => {
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Partner name is required';
    if (!form.partner_type) errs.partner_type = 'Type is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    return errs;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const fd = new FormData();
    fd.append('authenticity_token', csrfToken);
    Object.entries(form).forEach(([k, v]) => fd.append(`partner[${k}]`, v));
    if (logoFile) fd.append('partner[logo]', logoFile);

    try { await savePartner(fd); } finally { setSaving(false); }
  };

  const footer = (handleClose) => (
    <>
      <button type="button" className="btn btn-outline-secondary" onClick={handleClose}>Cancel</button>
      <button type="button" className="btn btn-success" disabled={saving} onClick={handleSubmit}>
        {saving ? 'Saving...' : 'Save Partner'}
      </button>
    </>
  );

  return (
    <AnimatedModal title={isEdit ? 'Edit Partner' : 'Add New Partner'} onClose={closeModal} footer={footer}>
      <div className="form-group">
        <label>Partner Logo</label>
        <ImageDropzone preview={logoPreview} onFileSelect={handleFileSelect} hint="Recommended: Square logo (400x400px)" />
      </div>

      <div className="form-group">
        <label>Partner Name <span className="text-danger">*</span></label>
        <input type="text" className={`form-control${errors.name ? ' is-invalid' : ''}`} name="name" value={form.name} onChange={handleChange} placeholder="Enter partner name" />
        <i className="form-group__bar" />
        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
      </div>

      <div className="form-group">
        <label>Type <span className="text-danger">*</span></label>
        <select className={`form-control${errors.partner_type ? ' is-invalid' : ''}`} name="partner_type" value={form.partner_type} onChange={handleChange}>
          <option value="internal_hermina">Internal Hermina</option>
          <option value="external_partner">External Partner</option>
        </select>
        <i className="form-group__bar" />
        {errors.partner_type && <div className="invalid-feedback">{errors.partner_type}</div>}
      </div>

      <div className="form-group">
        <label>Description <span className="text-danger">*</span></label>
        <textarea className={`form-control${errors.description ? ' is-invalid' : ''}`} name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Enter description" />
        <i className="form-group__bar" />
        {errors.description && <div className="invalid-feedback">{errors.description}</div>}
      </div>

      <div className="form-group">
        <label>Website URL</label>
        <input type="text" className="form-control" name="website_url" value={form.website_url} onChange={handleChange} placeholder="https://example.com (optional)" />
        <i className="form-group__bar" />
      </div>

      <div className="form-group">
        <label>Contact Person</label>
        <input type="text" className="form-control" name="contact_person" value={form.contact_person} onChange={handleChange} placeholder="Enter contact person name (optional)" />
        <i className="form-group__bar" />
      </div>
    </AnimatedModal>
  );
};

export default PartnerFormModal;
